import PropTypes from 'prop-types';

import { useEffect, useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { useSelector } from 'react-redux';

import useClickOutsidePopper from 'hooks/useClickOutsidePopper';
import useDebounce from 'hooks/useDebounce';
import useMediaQuery from 'hooks/useMediaQuery';

import { Breakpoints } from 'utils/const.styles';

import ReactPortal from './ReactPortal';
import * as S from './styled/Popper.styled';

/**
 * @author Burak Unsal
 */
const Popper = (props) => {
	const {
		refElement,
		placement: placementProp = 'bottom-start',
		showArrow,
		arrowColor,
		onClose,
		children,
		hoverMode: hoverModeProp = false,
		offsetX = 0,
		offsetY = 8,
		containerCss,
		containerClassName,
		strategy = 'absolute',
		wrapperId = 'popper',
		scrollRef,
		timeout = 0,
		closeTimeout = 0,
		alwaysShow = false,
		disableMobileView = false,
		showOnMobile = true
	} = props;

	const buttonHoverTimeout = useRef(null);
	const panelHoverTimeout = useRef(null);

	const isRtl = useSelector((state) => state.layout.localization.dir) === 'rtl';
	const [open, setOpen] = useState(false);
	const [buttonHover, setButtonHover] = useState(false);
	const [panelHover, setPanelHover] = useState(false);
	const [popperElement, setPopperElement] = useState(null);
	const [arrowElement, setArrowElement] = useState(null);

	const isMobile = useMediaQuery(Breakpoints.mobile);
	const hoverMode = isMobile ? false : hoverModeProp;
	const isOpen = hoverMode ? buttonHover || panelHover : open;
	const debouncedIsOpen = useDebounce(isOpen, timeout);
	const calculatedIsOpen = timeout > 0 ? debouncedIsOpen : isOpen;
	const placement = isRtl ? (popperRtlPlacement[placementProp] ?? placementProp) : placementProp;

	const popperModifiers = useMemo(() => {
		const modifiers = [
			{
				name: 'offset',
				options: {
					offset: [offsetX, offsetY]
				}
			},
			{
				name: 'preventOverflow',
				options: {
					altAxis: true
				}
			}
		];
		!isMobile && showArrow && modifiers.push({ name: 'arrow', options: { element: arrowElement } });
		return modifiers;
	}, [offsetX, offsetY, arrowElement]);

	const { styles, attributes, update } = usePopper(refElement, popperElement, {
		placement,
		strategy,
		modifiers: popperModifiers
	});

	const closePopper = () => {
		if (!open && !panelHover) return;
		onClose && onClose();
		setOpen(false);
		setPanelHover(false);
	};

	const handleClickOutside = (e) => {
		if (!open) return;
		e.preventDefault();
		e.stopPropagation();
		closePopper();
	};

	const handleButtonClick = () => {
		if (open) {
			onClose && onClose();
			setOpen(false);
		} else {
			setOpen(true);
		}
	};

	const handleButtonMouseEnter = () => {
		if (buttonHoverTimeout.current) {
			clearTimeout(buttonHoverTimeout.current);
		}

		setButtonHover(true);
	};

	const handleButtonMouseLeave = () => {
		if (closeTimeout > 0) {
			buttonHoverTimeout.current = setTimeout(() => {
				setButtonHover(false);
			}, closeTimeout);
		} else {
			setButtonHover(false);
		}
	};

	const handlePanelMouseEnter = () => {
		if (panelHoverTimeout.current) {
			clearTimeout(panelHoverTimeout.current);
		}

		setPanelHover(true);
	};

	const handlePanelMouseLeave = () => {
		if (closeTimeout > 0) {
			panelHoverTimeout.current = setTimeout(() => {
				setPanelHover(false);
			}, closeTimeout);
		} else {
			setPanelHover(false);
		}
	};

	useEffect(() => {
		if (!refElement) {
			return;
		}

		if (calculatedIsOpen) {
			refElement.classList.add(`${wrapperId}_open`);
		} else if (refElement.classList?.contains(`${wrapperId}_open`)) {
			refElement.classList.remove(`${wrapperId}_open`);
		}
	}, [calculatedIsOpen]);

	useEffect(() => {
		if (!refElement) {
			return;
		}

		if (hoverMode && !alwaysShow && refElement) {
			refElement.addEventListener('mouseenter', handleButtonMouseEnter);
			refElement.addEventListener('mouseleave', handleButtonMouseLeave);
		}

		return () => {
			refElement.removeEventListener('mouseenter', handleButtonMouseEnter);
			refElement.removeEventListener('mouseleave', handleButtonMouseLeave);
		};
	}, [hoverMode, alwaysShow, refElement, buttonHover]);

	useEffect(() => {
		if (!refElement || (isMobile && !showOnMobile)) {
			return;
		}

		if (refElement && !alwaysShow && !hoverMode) {
			refElement.addEventListener('click', handleButtonClick);
		}

		return () => {
			if (refElement && !alwaysShow && !hoverMode) {
				refElement.removeEventListener('click', handleButtonClick);
			}
		};
	}, [refElement, open]);

	const updatePosition = () => {
		update && update();
	};

	useClickOutsidePopper(refElement, popperElement, handleClickOutside, alwaysShow || hoverMode);

	const handleScroll = () => {
		setButtonHover(false);
		setPanelHover(false);
		setOpen(false);
	};

	useEffect(() => {
		if (scrollRef && calculatedIsOpen) {
			scrollRef.current.addEventListener('scroll', handleScroll);
		}

		return () => {
			if (scrollRef && scrollRef.current) {
				scrollRef.current.removeEventListener('scroll', handleScroll);
			}
		};
	}, [scrollRef, calculatedIsOpen]);

	return (
		<>
			{(calculatedIsOpen || alwaysShow) && (
				<ReactPortal wrapperId={wrapperId}>
					{isMobile && !disableMobileView && (calculatedIsOpen || alwaysShow) && <S.Shadow />}
					<S.ContentWrapper
						ref={setPopperElement}
						disableMobileView={disableMobileView}
						onMouseEnter={hoverMode ? handlePanelMouseEnter : undefined}
						onMouseLeave={hoverMode ? handlePanelMouseLeave : undefined}
						css={containerCss}
						style={styles.popper}
						className={containerClassName}
						{...attributes.popper}
						data-popper-arrow>
						{children(closePopper, updatePosition)}
						{!isMobile && showArrow && (
							<S.Arrow
								ref={setArrowElement}
								style={styles.arrow}
								arrowColor={arrowColor}
								{...attributes.popper}
							/>
						)}
					</S.ContentWrapper>
				</ReactPortal>
			)}
		</>
	);
};

const popperRtlPlacement = {
	left: 'right',
	right: 'left',
	'left-start': 'right-start',
	'left-end': 'right-end',
	'right-start': 'left-start',
	'right-end': 'left-end',
	'top-start': 'top-end',
	'top-end': 'top-start',
	'bottom-start': 'bottom-end',
	'bottom-end': 'bottom-start'
};

export default Popper;

Popper.propTypes = {
	/** Virtual reference element for the positioning popper container. */
	refElement: PropTypes.object,
	/** Popper placement. Default value is 'bottom-start'. Available options are 'auto', 'top', 'right', 'bottom' and 'left'. Also all options has 3 mode for example: 'bottom-start', 'bottom' and 'bottom-end'.  */
	placement: PropTypes.string,
	/** If true show arrow of the container */
	showArrow: PropTypes.bool,
	/** Color of the arrow */
	arrowColor: PropTypes.string,
	/** Call function when close popper. */
	onClose: PropTypes.func,
	/** If true activates hover mode. */
	hoverMode: PropTypes.bool,
	/** X offset of the container between reference element. */
	offsetX: PropTypes.number,
	/** Y offset of the container between reference element. */
	offsetY: PropTypes.number,
	/** Popper container css */
	containerCss: PropTypes.object,
	/** Popper position strategy. Default values is "absolute". Available options are "absolute" and "fixed". */
	strategy: PropTypes.string,
	/** Wrapper id of the react portal. */
	wrapperId: PropTypes.string,
	/** Closes popper when user scrolls the scroll reference object. */
	scrollRef: PropTypes.object,
	/** If true always show popper. */
	alwaysShow: PropTypes.bool,
	/** Timeout for closing popper. */
	timeout: PropTypes.number,
	/** If true disables mobile view. */
	disableMobileView: PropTypes.bool,
	/** Timeout for closing popper on hover mode. */
	closeTimeout: PropTypes.number,
	/** If true shows popper on mobile view. */
	showOnMobile: PropTypes.bool
};
